import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export type EvidenceType = "clip" | "snapshot" | "bookmark";
export type EvidenceStatus = "pending" | "processing" | "ready" | "failed";

export interface EvidenceItem {
  id: string;
  camera_id: string;
  title: string;
  description?: string | null;
  evidence_type: EvidenceType;
  status: EvidenceStatus;
  locked: boolean;
  locked_reason?: string | null;
  watermarked: boolean;
  export_count: number;
  file_size_bytes?: number | null;
  duration_seconds?: number | null;
  started_at?: unknown;
  ended_at?: unknown;
  last_exported_at?: unknown;
  expires_at?: unknown;
  created_at?: unknown;
  created_by?: string | null;
}

export interface EvidenceList {
  items: EvidenceItem[];
  total: number;
}

export interface EvidenceCreateInput {
  camera_id: string;
  title: string;
  description?: string;
  evidence_type: EvidenceType;
  started_at: string;
  ended_at?: string;
}

const KEY = ["evidence"] as const;

export function useEvidenceList() {
  return useQuery({
    queryKey: [...KEY, "list"],
    queryFn: async () => {
      const { data } = await api.get<EvidenceList>("/api/v1/evidence");
      return data;
    },
  });
}

export function useCreateEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EvidenceCreateInput) => {
      const { data } = await api.post<EvidenceItem>(
        "/api/v1/evidence",
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useLockEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const { data } = await api.post<EvidenceItem>(
        `/api/v1/evidence/${input.id}/lock`,
        { reason: input.reason },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUnlockEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<EvidenceItem>(
        `/api/v1/evidence/${id}/unlock`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/evidence/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// Batch 1 (UX/IA) — watermark is now a caller choice (was hardcoded false).
// Defaults to TRUE: for a bank, an exported evidence clip should carry the
// tamper-evident watermark unless an operator deliberately opts out.
export function useExportEvidence() {
  return useMutation({
    mutationFn: async (input: string | { id: string; watermark?: boolean }) => {
      const id = typeof input === "string" ? input : input.id;
      const watermark =
        typeof input === "string" ? true : input.watermark ?? true;
      const { data } = await api.post<{ download_url: string }>(
        `/api/v1/evidence/${id}/export`,
        { watermark },
      );
      return data;
    },
  });
}

export interface EvidenceExportLogItem {
  id: string;
  export_type: string;
  exported_by: string;
  ip_address?: string | null;
  shared: boolean;
  downloaded_at?: unknown;
  created_at?: unknown;
}

// Batch 1 (UX/IA) — the export/access trail for the chain-of-custody panel.
export function useEvidenceExports(id: string | null) {
  return useQuery({
    queryKey: [...KEY, "exports", id],
    queryFn: async () => {
      const { data } = await api.get<{
        items: EvidenceExportLogItem[];
        total: number;
      }>(`/api/v1/evidence/${id}/exports`);
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useShareEvidence() {
  return useMutation({
    mutationFn: async (input: { id: string; expires_in_hours: number }) => {
      const { data } = await api.post<{
        share_url: string;
        share_token: string;
        expires_at?: unknown;
      }>(`/api/v1/evidence/${input.id}/share`, {
        expires_in_hours: input.expires_in_hours,
      });
      return data;
    },
  });
}

// -------- Case folders --------
export interface CaseFolder {
  id: string;
  title: string;
  description?: string | null;
  status: "open" | "closed" | "archived";
  item_count: number;
  created_at?: unknown;
  updated_at?: unknown;
}

export interface CaseItem {
  id: string;
  evidence_id?: string | null;
  clip_mark_id?: string | null;
  note?: string | null;
  added_by: string;
  added_at?: unknown;
}

export interface CaseDetail extends CaseFolder {
  items: CaseItem[];
}

const CASES_KEY = ["evidence", "cases"] as const;

export function useCaseFolders() {
  return useQuery({
    queryKey: CASES_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ items: CaseFolder[]; total: number }>(
        "/api/v1/evidence/cases",
      );
      return data;
    },
  });
}

export function useCaseDetail(id: string | null) {
  return useQuery({
    queryKey: [...CASES_KEY, id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<CaseDetail>(
        `/api/v1/evidence/cases/${id}`,
      );
      return data;
    },
  });
}

export function useCreateCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; description?: string }) => {
      const { data } = await api.post<CaseFolder>(
        "/api/v1/evidence/cases",
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CASES_KEY }),
  });
}

export function useAddCaseItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      caseId: string;
      evidence_id?: string;
      clip_mark_id?: string;
      note?: string;
    }) => {
      const { data } = await api.post<CaseItem>(
        `/api/v1/evidence/cases/${input.caseId}/items`,
        {
          evidence_id: input.evidence_id,
          clip_mark_id: input.clip_mark_id,
          note: input.note,
        },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CASES_KEY }),
  });
}
